import { FlyweightPoolStaleReferenceError } from './flyweight-pool-errors';

type FlyweightPoolParams<T extends object> = {
  /**
   * Method for emptying the object being maintained in the pool.
   * @remark It will be provided a reference whose values it must fill. Can
   * optionally be provided the result of {@link creator}.
   */
  initializer: (empty: Partial<T>) => void;
  /**
   * Optional method for creating the reference being maintained in the pool.
   * @remark Supports use cases such as arrays (vec2, vec3, vec4).
   */
  creator?: () => T;
  /**
   * Maximum size of the pool.
   */
  capacity?: number;
  /**
   * Ensures flyweights are only used in the frame they were allocated. Throws
   * an error otherwise.
   *
   * @description Only use this in development mode if you have a frame-locked
   * flyweight pool. Puts a JS proxy in front of flyweighted entries, which is
   * untenable for performance.
   */
  throwOnStaleReferences?: boolean;
};

type FlyweightTick = number & { __tick: symbol };
type FlyweightPoolEntry<T> = { tick: FlyweightTick; entry: T };

const tick = (n: number) => n as FlyweightTick;
const creator = <T extends object>(): T => {
  return {} as T;
};

export class FlyweightPool<T extends object> {
  /**
   * Keeps track of the next index we can steal from.
   *
   * By default, all objects are flyweighted. If an object is opted to be kept
   * stable, it is uplifted into a separate list.
   */
  private cursor = 0;

  /**
   * Keeps track of the current tick in a game loop. Used to identify stale
   * references in development mode to throw errors.
   */
  private tick: FlyweightTick = tick(1);

  /**
   * Provides the entry inside of a {@link FlyweightPoolEntry}. Used when
   * {@link throwOnStaleReferences} is set to wrap in a JS Proxy to propagate
   * errors if frame-locking has been violated on read.
   */
  private unwrap: (entry: FlyweightPoolEntry<T>) => T;

  /**
   * Allocations which can be stolen when {@link get} or {@link freeAll} is
   * called. Implements the flyweight pattern.
   * @remark {@link get} steals if maximum capacity has been met.
   * @remark {@link freeAll} clears out all flyweights.
   */
  private allocations: Array<FlyweightPoolEntry<T>> = [];

  /**
   * For creating a new entry reference
   */
  private creator: () => T;
  /**
   * For emptying out an entry reference
   */
  private initializer: (empty: Partial<T>) => void;

  private capacity: number;

  constructor(params: FlyweightPoolParams<T>) {
    this.initializer = params.initializer;
    this.creator = params.creator ?? creator;
    this.capacity = params.capacity ?? 500;
    this.unwrap = params.throwOnStaleReferences
      ? this.unwrapEntrySafe.bind(this)
      : this.unwrapEntry.bind(this);
  }

  /**
   * Uses the {@link initializer} function provided on construction to either
   * steal an existing flyweight if at capacity, or create a new object.
   * @description The lifetime of flyweights is only guaranteed between calls to
   * {@link get}. Hence, be cognizant of how you use them. Make sure to use
   * them in a way which is one-time-use only.
   * @returns a flyweighted object instance
   */
  get() {
    // get or create new ref
    const alloc = this.allocations[this.cursor] ?? {
      tick: this.tick,
      entry: this.creator(),
    };
    alloc.tick = this.tick;

    // init
    this.initializer(alloc.entry);
    this.allocations[this.cursor] = alloc;

    // increment
    this.cursor = this.cursor + 1 === this.capacity ? 0 : this.cursor + 1;

    // unwrap entry
    return this.unwrap(alloc);
  }

  onTick() {
    this.tick++;
  }

  freeAll() {
    for (const alloc of this.allocations) {
      // @ts-expect-error - use `null` to fully dereference and GC
      alloc.entry = null;
      void alloc;
    }

    this.allocations.length = 0;
  }

  /**
   * Performant unwrap with no lifecycle tracking
   * @param alloc
   * @returns entry
   */
  private unwrapEntry(alloc: FlyweightPoolEntry<T>) {
    return alloc.entry;
  }

  /**
   * Safer unwrap which lifecycle tracking, enforce frame-locking.
   * @param alloc
   * @returns entry
   */
  private unwrapEntrySafe(alloc: FlyweightPoolEntry<T>) {
    const current = () => this.tick;

    return new Proxy<T>(alloc.entry, {
      get(target, property, receiver) {
        const curr = current();
        if (alloc.tick !== curr) {
          throw new FlyweightPoolStaleReferenceError(
            `Flyweights are frame-locked. The flyweight you are referencing was allocated in tick ${alloc.tick} and used in tick ${curr}. This is problematic. Check to ensure you are freeing all references correctly.`,
          );
        }

        return Reflect.get(target, property, receiver);
      },
    });
  }
}
